import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './SideMenu.css';
import { goalService, authService, lessonService, roadmapService, userService, subscriptionService, learningPlanService, courseService } from '../services/api';
import { useTheme } from '../ThemeContext';
import { 
  Home, 
  MessageSquare, 
  Book, 
  Calendar, 
  Map, 
  Database, 
  Settings, 
  HelpCircle,
  Target,
  Menu,
  X,
  Sun,
  Moon
} from 'react-feather';

// Import avatar SVGs
import { ReactComponent as LOTRAvatar } from '../assets/avatars/lotr.svg';
import { ReactComponent as GOTAvatar } from '../assets/avatars/got.svg';
import { ReactComponent as CyberpunkAvatar } from '../assets/avatars/cyberpunk.svg';
import { ReactComponent as GTAAvatar } from '../assets/avatars/gta.svg';
import { ReactComponent as HackerAvatar } from '../assets/avatars/hacker.svg';
import { ReactComponent as StarWarsAvatar } from '../assets/avatars/starwars.svg';
import { ReactComponent as MarvelAvatar } from '../assets/avatars/marvel.svg';
import { ReactComponent as AnimeAvatar } from '../assets/avatars/anime.svg';
import { ReactComponent as PixelAvatar } from '../assets/avatars/pixel.svg';
import { ReactComponent as DisneyAvatar } from '../assets/avatars/disney.svg';

const SideMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  
  const [activeItem, setActiveItem] = useState('dashboard');
  const [userAvatar, setUserAvatar] = useState('lotr');
  const [username, setUsername] = useState('User');
  const [currentLesson, setCurrentLesson] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [activeCourseId, setActiveCourseId] = useState(null);
  
  // Subscription and lesson limit modal states
  const [subscriptionStatus, setSubscriptionStatus] = useState({ 
    tier: 'free', 
    isActive: false, 
    endDate: null 
  });
  const [showLessonLimitModal, setShowLessonLimitModal] = useState(false);
  const [roadmaps, setRoadmaps] = useState([]);
  const [lessons, setLessons] = useState({});
  const [activeGoal, setActiveGoal] = useState(null);

  // Helper function to check if user is on free plan
  const isUserOnFreePlan = subscriptionStatus.tier === 'free' || 
                           subscriptionStatus.tier === 'basic' ||
                           !subscriptionStatus.isActive;

  // Helper function to check if user has completed any lessons (topics)
  const hasCompletedAnyLesson = () => {
    if (!roadmaps || roadmaps.length === 0) return false;
    
    for (const roadmap of roadmaps) {
      const roadmapLessons = lessons[roadmap.Id] || [];
      if (roadmapLessons.some(lesson => lesson.IsCompleted)) {
        return true;
      }
    }
    return false;
  };
  
  // Check if screen is mobile
  useEffect(() => {
    const checkScreenSize = () => {
      const newIsMobile = window.innerWidth < 850;
      setIsMobile(newIsMobile);
      
      // Close mobile menu when switching to desktop
      if (!newIsMobile && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
      
      // Reset mobile menu state when switching to mobile
      if (newIsMobile && window.innerWidth >= 850) {
        setIsMobileMenuOpen(false);
      }
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    // Cleanup function
    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, [isMobileMenuOpen]);
  
  // Load user data from localStorage
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Fetch user data
        const userData = await userService.getUser();
        const savedAvatar = localStorage.getItem('userAvatar') || 'lotr';
        localStorage.setItem('username', userData.username);

        // Determine subscription tier
        try {
          const subStatus = await subscriptionService.getStatus();
          setIsPro(subStatus.tier === 'pro');
          setSubscriptionStatus(subStatus);
        } catch (_) {
          // Fail silently – show no badge
          setIsPro(false);
          setSubscriptionStatus({ tier: 'free', isActive: false, endDate: null });
        }

        setUserAvatar(savedAvatar);
        setUsername(userData.username);

      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    
    fetchUserData();


    // Set active menu item based on current path
    const path = location.pathname;
    if (path.includes('/learning-plan')) {
      setActiveItem('learning-plan');
    } else if (path.includes('/knowledge-graph')) {
      setActiveItem('knowledge-graph');
    } else if (path.includes('/goals')) {
      setActiveItem('journey');
    } else if (path.includes('/lessons')) {
      setActiveItem('current-lesson');
    } else if (path.includes('/roadmap')) {
      setActiveItem('roadmap');
    } else if (path.includes('/goals')) {
      setActiveItem('goals');
    } else if (path.includes('/chat')) {
      setActiveItem('chat');
    } else if (path.includes('/settings')) {
      setActiveItem('settings');
    } else if (path === '/' || path === '/home' || path.includes('/home')) {
      setActiveItem('home');
    }
    
    // Listen for avatar updates
    const handleAvatarUpdate = (event) => {
      setUserAvatar(event.detail.avatar);
    };
    
    // Listen for unauthorized access event
    const handleUnauthorizedAccess = () => {
      console.log('Unauthorized access event received in SideMenu');
      navigate('/login');
    };
    
    document.addEventListener('avatar-updated', handleAvatarUpdate);
    document.addEventListener('unauthorized-access', handleUnauthorizedAccess);
    const handleSubscriptionUpdate = (e) => {
      if (e.detail?.tier === 'pro') {
        setIsPro(true);
      } else {
        setIsPro(false);
      }
    };
    window.addEventListener('subscription-updated', handleSubscriptionUpdate);
    
    return () => {
      document.removeEventListener('avatar-updated', handleAvatarUpdate);
      document.removeEventListener('unauthorized-access', handleUnauthorizedAccess);
      window.removeEventListener('subscription-updated', handleSubscriptionUpdate);
    };
  }, [location, navigate]);

  const handleCurrentLessonClick = async () => {
    try {
      // Show loading state
      document.dispatchEvent(new CustomEvent('current-lesson-loading', { detail: { loading: true } }));
        // Fetch current lesson
      try {
        const lessonData = await lessonService.getCurrentLesson();
        if (lessonData) {
          setCurrentLesson(lessonData);
        }
        
        if (lessonData && lessonData.id) {
            navigate(`/lessons/${lessonData.id}`);
        } else {
        }
      } catch (lessonError) {
        console.error('Error fetching current lesson:', lessonError);
      }
    } catch (error) {
      console.error('Error navigating to current lesson:', error);
      // Hide loading state on error
      document.dispatchEvent(new CustomEvent('current-lesson-loading', { detail: { loading: false } }));
    }
    // Close mobile menu after navigation
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  };

  // Lesson limit modal functions
  const closeLessonLimitModal = () => {
    setShowLessonLimitModal(false);
  };

  const handleGetPro = () => {
    closeLessonLimitModal();
    navigate('/settings', { state: { activeTab: 'subscription' } });
  };
  
  const menuItems = [
    { 
      id: 'home', 
      label: 'Home', 
      icon: Home,
      onClick: () => {
        navigate('/home');
        if (isMobile) setIsMobileMenuOpen(false);
      }
    },
    { 
      id: 'current-lesson', 
      label: 'Current Lesson', 
      icon: Book,
      onClick: handleCurrentLessonClick
    },
    {
      id: 'learning-plan',
      label: 'Courses',
      icon: Target,
      onClick: () => {
          navigate('/learning-plan');
        if (isMobile) setIsMobileMenuOpen(false);
      }
    },
    { 
      id: 'journey', 
      label: 'Roadmap', 
      icon: Map,
      onClick: () => {
        navigate('/goals');
        if (isMobile) setIsMobileMenuOpen(false);
      }
    },
    { 
      id: 'knowledge-graph', 
      label: 'Notes', 
      icon: Database,
      onClick: () => {
        navigate('/knowledge-graph');
        if (isMobile) setIsMobileMenuOpen(false);
      }
    },
    // { 
    //   id: 'goals', 
    //   label: 'Goals', 
    //   icon: Target,
    //   onClick: () => {
    //     navigate('/goals');
    //     if (isMobile) setIsMobileMenuOpen(false);
    //   }
    // },
    { 
      id: 'settings', 
      label: 'Settings', 
      icon: Settings,
      onClick: () => {
        navigate('/settings');
        if (isMobile) setIsMobileMenuOpen(false);
      }
    }
  ];

  const handleLogout = () => {
    authService.logout();
    navigate('/');
  };
  
  // Map of avatar components
  const avatarComponents = {
    lotr: LOTRAvatar,
    got: GOTAvatar,
    cyberpunk: CyberpunkAvatar,
    gta: GTAAvatar,
    hacker: HackerAvatar,
    starwars: StarWarsAvatar,
    marvel: MarvelAvatar,
    anime: AnimeAvatar,
    pixel: PixelAvatar,
    disney: DisneyAvatar
  };
  
  // Get the current avatar component
  const AvatarComponent = avatarComponents[userAvatar] || LOTRAvatar;
  
  const openTerms = () => {
    window.open('/terms', '_blank');
  };

  const openPrivacy = () => {
    window.open('/privacy', '_blank');
  };

  // Mobile hamburger menu toggle
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Mobile top menu component
  if (isMobile) {
    return (
      <>
        {/* Mobile Top Menu Bar - Always visible on mobile */}
        <div className={`sidemenu-mobile-top-bar ${theme}`}>
          {/* Mobile Menu Header - Always displayed */}
          <div className="sidemenu-mobile-header">
            <div className="sidemenu-mobile-logo">
              <span className="sidemenu-mobile-product-name">Brainy {isPro && <span className="pro-badge">PRO</span>}</span>
            </div>
            <div className={`sidemenu-mobile-header-actions ${theme}`}>
              {/* Get Pro Button for Mobile - Only show for non-pro users */}
              {!isPro && (
                <button 
                  className={`sidemenu-mobile-get-pro-button ${theme}`}
                  onClick={() => {
                    navigate('/settings');
                    if (isMobile) setIsMobileMenuOpen(false);
                  }}
                >
                  Get Pro
                </button>
              )}
              
              {/* Theme Toggle for Mobile */}
              <div className={`sidemenu-mobile-theme-toggle ${theme}`} onClick={toggleTheme}>
                <div className="sidemenu-mobile-toggle-track">
                  <div className="sidemenu-mobile-toggle-thumb">
                    <Sun className={`sidemenu-toggle-icon sidemenu-toggle-icon-sun ${theme === 'dark' ? 'active' : ''}`} size={14} />
                    <Moon className={`sidemenu-toggle-icon sidemenu-toggle-icon-moon ${theme === 'light' ? 'active' : ''}`} size={14} />
                  </div>
                </div>
              </div>
              
              <button 
                className="sidemenu-mobile-menu-toggle"
                onClick={toggleMobileMenu}
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
          
          {/* Mobile Menu Items - Only shown when menu is open */}
          {isMobileMenuOpen && (
            <div className={`sidemenu-mobile-menu-overlay ${theme}`}>
              <div className="sidemenu-mobile-menu-content">
                <div className="sidemenu-mobile-user-info">
                  <div className="sidemenu-mobile-user-avatar">
                    <AvatarComponent />
                  </div>
                  <span className={`sidemenu-mobile-username ${theme}`}>{username}</span>
                </div>
                
                <div className="sidemenu-mobile-menu-items">
                  {menuItems.map(item => (
                    <div 
                      key={item.id}
                      className={`sidemenu-mobile-menu-item ${activeItem === item.id ? 'active' : ''}`}
                      onClick={item.onClick}
                    >
                      <item.icon className="sidemenu-mobile-menu-icon" size={20} />
                      <span className="sidemenu-mobile-menu-label">{item.label}</span>
                    </div>
                  ))}
                </div>
                
                <div className="sidemenu-mobile-menu-footer">
                  <div className="sidemenu-mobile-logout-btn" onClick={handleLogout}>
                    <i className="material-icons">logout</i>
                    <span>Logout</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Lesson Limit Modal */}
        <div className={`roadmap-modal ${showLessonLimitModal ? 'show' : ''}`}>
          <div className={`modal-content ${theme}`}>
            <div className="modal-header">
              <h3 className="modal-title">Free Plan Limit Reached</h3>
              <button 
                className="close-button" 
                onClick={closeLessonLimitModal}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="lesson-limit-warning">
                <div className="limit-icon">⚠️</div>
                <div className="limit-message">
                  <p>You've completed 1 topic in this goal. Free plan users can only complete 1 topic per goal.</p>
                </div>
                <div className="limit-suggestion">
                  <p>To continue learning and unlock unlimited topics, upgrade to Pro and get:</p>
                </div>
                <div className="limit-features">
                  <h4>Pro Features:</h4>
                  <ul>
                    <li>✨ Up to 10 goals in progress</li>
                    <li>💬 Unlimited AI Chat Queries</li>
                    <li>📚 Unlimited AI Lessons</li>
                    <li>📝 Unlimited AI Assessments</li>
                    <li>🤖 Best AI Models</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-button" 
                onClick={closeLessonLimitModal}
              >
                Maybe Later
              </button>
              <button 
                className="confirm-button upgrade" 
                onClick={handleGetPro}
              >
                Upgrade to Pro
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }
  
  // Desktop side menu (original design)
  return (
    <div className={`side-menu ${theme}`}>
      <div className="logo-container">
        <div className="logo" style={{margin: '0px'}}>
          <span className="product-name">Brainy {isPro && <span className="pro-badge">PRO</span>}</span>
        </div>
      </div>
      
      <div className="side-menu-items">
        <div className="menu-section">
          {menuItems.map(item => (
            <div 
              key={item.id}
              className={`side-menu-item ${activeItem === item.id ? 'active' : ''}`}
              onClick={item.onClick}
            >
              <item.icon className="side-menu-icon" size={20} />
              <span className="side-menu-label">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
      
      <div className="side-menu-footer">
        <div className="sign-out-btn" onClick={handleLogout}>
          <i className="material-icons sign-out-icon">logout</i>
          <span>Logout</span>
        </div>
        
        <div className="policy-links">
          <span onClick={openTerms}>Terms of Use</span>
          <span className="link-separator">and</span>
          <span onClick={openPrivacy}>Privacy Policy</span>
        </div>
      </div>
      
      {/* Lesson Limit Modal */}
      <div className={`roadmap-modal ${showLessonLimitModal ? 'show' : ''}`}>
        <div className={`modal-content ${theme}`}>
          <div className="modal-header">
            <h3 className="modal-title">Free Plan Limit Reached</h3>
            <button 
              className="close-button" 
              onClick={closeLessonLimitModal}
            >
              ×
            </button>
          </div>
          <div className="modal-body">
            <div className="lesson-limit-warning">
              <div className="limit-icon">⚠️</div>
              <div className="limit-message">
                <p>You've completed 1 topic in this goal. Free plan users can only complete 1 topic per goal.</p>
              </div>
              <div className="limit-suggestion">
                <p>To continue learning and unlock unlimited topics, upgrade to Pro and get:</p>
              </div>
              <div className="limit-features">
                <h4>Pro Features:</h4>
                <ul>
                  <li>✨ Up to 10 goals in progress</li>
                  <li>💬 Unlimited AI Chat Queries</li>
                  <li>📚 Unlimited AI Lessons</li>
                  <li>📝 Unlimited AI Assessments</li>
                  <li>🤖 Best AI Models</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button 
              className="cancel-button" 
              onClick={closeLessonLimitModal}
            >
              Maybe Later
            </button>
            <button 
              className="confirm-button upgrade" 
              onClick={handleGetPro}
            >
              Upgrade to Pro
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SideMenu; 